import { FieldPolicy, FieldReadFunction, InMemoryCache } from "@apollo/client";
import { CacheQueryTypes } from "./cacheQueryTypes";
import { rootQueryTypePolicy, withFields } from "./util";

const queryFields = Object.entries(CacheQueryTypes).reduce<{
  [fieldName: string]: FieldPolicy<any> | FieldReadFunction<any>;
}>((prev, current) => {
  return {
    ...prev,
    [current[0]]: {
      read: (existing) => existing ?? { __typename: current[1] },
    },
  };
}, {});

export default () =>
  new InMemoryCache({
    typePolicies: {
      Query: {
        fields: queryFields,
      },
      AccountMetricQuery: rootQueryTypePolicy([]),
      AddressQuery: rootQueryTypePolicy([]),
      AnalyticsQuery: rootQueryTypePolicy([]),
      AgentQuery: rootQueryTypePolicy(
        ["sessions", "messages"],
        [{ query: "get", GType: "AgentSessionType" }],
      ),
      // Neither has an `id` on the backend: one is a code artifact (the agent
      // picker), the other is a transient stream event. Without this Apollo warns
      // and mis-normalizes them.
      AgentDefinitionType: { keyFields: false },
      // Per-user computed results with no `id` on the backend.
      AccountDeletionPreflightType: { keyFields: false },
      AccountDeletionBlockerType: { keyFields: false },
      AmbassadorMovementsType: { keyFields: false },
      AmbassadorMovementGroupType: { keyFields: false },
      AgentStreamEventType: { keyFields: false },
      // The campaign a browser with no account started: derived from its session
      // cookie, no `id` on the backend.
      GuestCurrentType: { keyFields: false },
      // The email sign-up awaiting its link, derived from the same session cookie.
      GuestPendingSignUpType: { keyFields: false },
      AfiliateProgramQuery: rootQueryTypePolicy(
        ["list", "listProgramUsers", "sales"],
        [
          { query: "sale", GType: "AfiliateProgramUserSaleType" },
          { query: "getAfiliateProgramUser", GType: "AfiliateProgramUserType" },
          { query: "get", GType: "AfiliateProgramType" },
        ],
      ),
      AfiliateProgramPostulationQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "AfiliateProgramPostulationType" }],
      ),
      AppLabelQuery: rootQueryTypePolicy([]),
      BrandQuery: rootQueryTypePolicy(
        ["userBrands"],
        [{ query: "get", GType: "BrandType" }],
      ),
      BadgeQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "BadgeType" }],
      ),
      CourseQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "CourseType" }],
      ),
      CommunityQuery: rootQueryTypePolicy(
        ["communityPosts"],
        [{ query: "communityPost", GType: "CommunityPostType" }],
      ),
      ContentApprovalQuery: rootQueryTypePolicy(["list"]),
      CourseCategoryQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "CourseCategoryType" }],
      ),
      CourseProgressQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "CourseProgressType" }],
      ),
      EventQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "EventType" }],
      ),
      LessonQuery: rootQueryTypePolicy(
        [],
        [{ query: "get", GType: "LessonType" }],
      ),
      LessonProgressQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "LessonProgressType" }],
      ),
      SkillQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "SkillType" }],
      ),
      TeacherQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "TeacherType" }],
      ),
      UserBadgeQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "UserBadgeType" }],
      ),
      UserSkillQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "UserSkillType" }],
      ),
      CampaignDraftQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "CampaignDraftType" }],
      ),
      CampaignQuery: rootQueryTypePolicy(
        ["list"],
        [
          { query: "get", GType: "CampaignType" },
          {
            query: "getCampaignPreselection",
            GType: "CampaignPreselectionType",
          },
        ],
      ),
      ChatQuery: rootQueryTypePolicy(
        ["list", "listMessages", "chats", "chatMessages", "chatParticipants"],
        [{ query: "get", GType: "ChatType" }],
      ),
      ClassifiersType: { keyFields: false },
      ClassifierContent: { keyFields: false },
      ClassifiersFacet: { keyFields: false },
      AlignedCreatorsType: { keyFields: false },
      CreatorMatchType: { keyFields: false },
      CreatorSanctionType: { keyFields: false },
      EcommerceQuery: rootQueryTypePolicy([
        "listOffices",
        "listProducts",
        "listDiscountCodes",
        "listCollections",
        "affiliateDiscountCodes",
      ]),
      CountryCityQuery: rootQueryTypePolicy([]),
      // Onboarding country social proof — carries no `id` on the backend.
      AvailableCountryTestimonialType: { keyFields: false },
      // Onboarding brand lookup previews — computed per request (website
      // metadata scrape / Instagram business-discovery call), not persisted
      // rows, so neither carries an `id`.
      WebsiteInfoType: { keyFields: false },
      InstagramProfilePreviewType: { keyFields: false },
      GamificationQuery: rootQueryTypePolicy([]),
      GenericEnumsQuery: rootQueryTypePolicy(
        [],
        [{ query: "postType", GType: "PostTypeType" }],
      ),
      // The guest chat's twin of the namespaces above (`agent`, `brand`, `product`...):
      // the same fields, read by id from the session cookie's campaign.
      GuestQuery: rootQueryTypePolicy(
        ["messages", "products"],
        [
          { query: "agentSession", GType: "AgentSessionType" },
          { query: "brand", GType: "BrandType" },
          { query: "campaignDraft", GType: "CampaignDraftType" },
          { query: "product", GType: "ProductType" },
        ],
      ),
      InvoiceQuery: rootQueryTypePolicy(["list"]),
      // Currency aggregate for the billing screen's per-currency tabs — a
      // computed rollup, not a persisted row, so it carries no `id`.
      InvoiceCurrencySummaryType: { keyFields: false },
      // Public `/invite` page preview of a workspace invitation, read by token:
      // a projection of the invitation, not a row, so it carries no `id`.
      WorkspaceInvitationPreviewType: { keyFields: false },
      MobileVersionQuery: rootQueryTypePolicy([]),
      RecommendationQuery: rootQueryTypePolicy([
        "campaignRecommendations",
        "campaignInvitations",
        "creatorRecommendations",
      ]),
      PlatformQuery: rootQueryTypePolicy([]),
      PostQuery: rootQueryTypePolicy(["comments", "posts"]),
      PostulationTagQuery: rootQueryTypePolicy([]),
      PostulationQuery: rootQueryTypePolicy(
        ["postulations", "campaignPostulations"],
        [{ query: "get", GType: "PostulationType" }],
      ),
      ProductQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "ProductType" }],
      ),
      ReportQuery: rootQueryTypePolicy([]),
      ReviewsQuery: rootQueryTypePolicy([]),
      SocialNetworkUserQuery: rootQueryTypePolicy(
        [],
        [{ query: "get", GType: "SocialNetworkUserType" }],
      ),
      SubscriptionsQuery: rootQueryTypePolicy([]),
      TaxQuery: rootQueryTypePolicy([]),
      FiscalQuery: rootQueryTypePolicy([]),
      UserQuery: rootQueryTypePolicy(
        ["list"],
        [{ query: "get", GType: "UserType" }],
      ),
      WalletQuery: rootQueryTypePolicy(["transactions"]),
      WorkOrderQuery: withFields(
        rootQueryTypePolicy(
          // `details` is the analytics report's content gallery. Without a
          // pagination policy its `fetchMore` overwrote the first page with the
          // second instead of appending, so "Cargar más" appeared to do nothing.
          ["list", "contentApprovals", "details"],
          [
            { query: "get", GType: "WorkOrderType" },
            { query: "getWorkOrderDetail", GType: "WorkOrderDetailType" },
          ],
        ),
        { getForUser: { keyArgs: ["campaignId", "userId"] } },
      ),
      BankAccountQuery: rootQueryTypePolicy([]),
      NotificationQuery: rootQueryTypePolicy(["myNotification"]),
      NotificationDestinationType: { keyFields: false },
      PaymentsQuery: rootQueryTypePolicy([]),
      WorkspaceQuery: rootQueryTypePolicy([]),
      WorkspaceCreatorQuery: rootQueryTypePolicy(["list"]),
      Identifier: { keyFields: false },
      Money: {
        keyFields: false,
      },
      UploadContentApprovalParams: {
        keyFields: false,
      },
      Currency: {
        keyFields: false,
      },
      FileFieldType: {
        keyFields: false,
      },
      DateFieldType: {
        keyFields: false,
      },
      PostulationValidationType: {
        keyFields: false,
      },
      CampaignProductionHealthType: {
        keyFields: false,
      },
      CampaignProgressType: {
        keyFields: false,
      },
      CommentSentimentDistributionType: {
        keyFields: false,
      },
      PostCommentsSummaryType: {
        keyFields: false,
      },
      CommentThemeType: {
        keyFields: false,
      },
      CampaignCommentsAnalysisType: {
        keyFields: false,
      },
      ReportDemographicsType: {
        keyFields: false,
      },
      DemographicSliceType: {
        keyFields: false,
      },
      DateTimeRangeType: {
        keyFields: false,
      },
      StoreVisitDayType: { keyFields: false },
      StoreVisitSlotType: { keyFields: false },
      PeriodCheckoutType: { keyFields: false },
      PeriodTax: { keyFields: false },
      CurrentTotalSalesPerPeriodType: { keyFields: false },
      TransitionConditionsType: { keyFields: false },
      LevelProgressType: { keyFields: false },
      CommunityAuthorType: { keyFields: false },
      TopSellerType: { keyFields: false },
      OrganicCampaignPlanType: { keyFields: false },
      OrganicCampaignPlanResultType: { keyFields: false },
      OrganicCampaignPlanCreatorType: { keyFields: false },
      OrganicCampaignPlanDistributionItemType: { keyFields: false },
      CampaignPendingTasksType: { keyFields: false },
      AfiliateProgramTotalValuesType: { keyFields: false },
      BrandDashboardType: { keyFields: false },
      ScriptOutputType: { keyFields: false },
      CenterTotalValuesType: { keyFields: false },
      EcommerceDashboardStatisticsType: { keyFields: false },
      DashboardMetric: { keyFields: false },
      DashboardSocialNetworkMetricsType: { keyFields: false },
      QuotaDictType: { keyFields: false },
      ReviewSearch: { keyFields: false },
      ReviewSummary: { keyFields: false },
      AfiliateProgramRankingType: { keyFields: false },
      AfiliateProgramUserSaleInvalidReasonType: { keyFields: false },
      CreatorPaymentBreakdownType: { keyFields: false },
      // Fiscal requirements + regime option are computed per country from the
      // tax policy classes, not persisted rows — neither carries an `id`.
      FiscalRequirementsType: { keyFields: false },
      TaxRegimeOptionType: { keyFields: false },
      // One field of a country's tax profile form — part of the policy
      // description, no `id`.
      TaxProfileFieldType: { keyFields: false },
      // Registry lookup result (SII match by RUT) — a lookup response, not a
      // persisted row, so it has no `id`.
      TaxIdRegistryMatchType: { keyFields: false },
      // One document type a country's identifier input offers — computed from
      // the identifier handlers, no `id`. Keyed by nothing: the list is
      // always read whole per country.
      IdentifierTypeOptionType: { keyFields: false },
      // Checkout summaries and their payment breakdown lines — computed per
      // request from the tax/fee policies, not persisted rows.
      AffiliateCheckoutType: { keyFields: false },
      CheckoutType: { keyFields: false },
      PaymentBreakdownType: { keyFields: false },
      PaymentLineType: { keyFields: false },
      PaymentOptionType: { keyFields: false },
      WithdrawalBreakdownType: { keyFields: false },
      WithdrawalDocumentSpecType: { keyFields: false },
      CampaignAnalyticType: {
        keyFields: false,
      },
      CampaignOverviewReachType: {
        keyFields: false,
      },
      CampaignCreatorAnalyticType: {
        keyFields: false,
      },
      CampaignCreatorAnalyticsDetailType: {
        keyFields: false,
      },
      PendingPaymentDetailType: {
        keyFields: false,
      },
      LessonSubmissionUploadParams: {
        keyFields: false,
      },
      CancellationReimbursementType: {
        keyFields: false,
      },
      SalesTimelinePointType: { keyFields: false },
      ScoreBreakdownCategoryType: { keyFields: false },
      ScoreBreakdownType: { keyFields: false },
      ReasonType: { keyFields: false },
      TotalSalesType: { keyFields: false },
      BrandSocialImpactType: { keyFields: false },
      PlatformImpactType: { keyFields: false },
      BrandImpactWindowType: { keyFields: false },
      BrandImpactDayPointType: { keyFields: false },
      EcommerceImpactType: { keyFields: false },
      EcommercePlatformImpactType: { keyFields: false },
      EcommerceDayPointType: { keyFields: false },
      CampaignProjectionType: { keyFields: false },
      ProjectionMetricType: { keyFields: false },
      ProjectionMoneyMetricType: { keyFields: false },
      ExploreContentRequirementType: { keyFields: false },
      // `analytics.consolidatedReport` aggregate — none of these carry an
      // `id`, they're computed in-memory per selection, not persisted rows.
      ConsolidatedReportType: { keyFields: false },
      CompositionSegmentType: { keyFields: false },
      CampaignContributionType: { keyFields: false },
      EvolutionPointType: { keyFields: false },
      ConsolidatedOrganicType: { keyFields: false },
      ConsolidatedProductionType: { keyFields: false },
      // `ConsolidatedPostType` rows are keyed by `detailId`, not `id`; the
      // creator rows are keyed by `userId` (a creator can contribute to
      // several selected campaigns, so their row is a merge, not one DB row).
      ConsolidatedPostType: { keyFields: false },
      ConsolidatedCreatorRowType: { keyFields: false },
      // Production ranking rows are keyed by `userId`, not `id`; the refresh
      // mutation's per-campaign cooldown entries have no `id` at all.
      ProductionRankingRowType: { keyFields: false },
      SkippedCampaignType: { keyFields: false },
      // `analytics.brandAnalytics` aggregate — computed per brand/platform/days
      // request, none of these are persisted rows with an `id`.
      BrandAnalyticsType: { keyFields: false },
      BrandAccountType: { keyFields: false },
      BrandKpiType: { keyFields: false },
      BrandSeriesPointType: { keyFields: false },
      // `address.addressFormat` — the area levels and postal code format of a
      // country, computed per request; neither type has an `id`.
      AddressFormatType: { keyFields: false },
      AddressFormatLevelType: { keyFields: false },
      // `banking.generateWithdrawalDocument` — a rendered document, never
      // stored; neither type has an `id`.
      WithdrawalDocumentDraftType: { keyFields: false },
      WithdrawalDocumentWithholdingType: { keyFields: false },
      // `workspaceplan.freeTrial` — computed from the workspace's plan, no `id`.
      FreeTrialType: { keyFields: false },
      // `tracking.pixelConfigs` — one entry per configured ad platform, no `id`.
      PixelConfigType: { keyFields: false },
    },
  });
